import { useMemo } from 'react';
import { AutomessagingOrg } from '@wetags/automessaging';
import { useListCampaignDrafts } from '@frontend/api-bulk-messaging';
import { useHasBulkEmailAccess, useHasBulkTextAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { createNavItem, createSubNavItems } from '../nav-item-utils';
import { featureDefinition as marketingFeatureDefinition } from './feature-definitions';

export const useMarketingNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const { hasBulkEmailAccess } = useHasBulkEmailAccess();
  const { hasBulkTextAccess } = useHasBulkTextAccess();
  const { data: draftsCount = 0 } = useListCampaignDrafts(
    {
      locationIds: selectedLocationIds,
      orgId: selectedOrgId,
      year: new Date().getFullYear(),
    },
    hasBulkEmailAccess,
    hasBulkTextAccess,
    {
      staleTime: 1000 * 60 * 5, // Keep drafts count fresh for 5 minutes to minimize refetching
      select: (campaigns) => campaigns.length, // Transform the count to reduce memory footprint
    }
  );

  const [showLeadEngage, isLoadingShowLeadEngage] = AutomessagingOrg.useWetag('enable-lead-engage-org');
  const marketingItems = marketingFeatureDefinition.items;

  const subNavItems = useMemo(() => {
    return createSubNavItems([
      {
        ...marketingItems['/all-campaigns'],
        label: t('All Campaigns'),
        relativePath: '/all-campaigns',
      },
      ...(!isLoadingShowLeadEngage && showLeadEngage
        ? [
            {
              ...marketingItems['/lead-engage'],
              label: t('Lead Connect'),
              relativePath: '/lead-engage',
            },
          ]
        : []),
      {
        ...marketingItems['/drafts'],
        label: t('Drafts'),
        relativePath: '/drafts',
        count: draftsCount,
        isPassiveCount: true,
      },
    ]);
  }, [draftsCount, isLoadingShowLeadEngage, showLeadEngage, t, marketingItems]);

  return useMemo(
    () =>
      createNavItem('marketing', {
        hasCount: !!draftsCount,
        iconName: 'bulk-messaging',
        label: t('Marketing'),
        root: '/bulk',
        show: true,
        subNavItems,
      }),
    [draftsCount, subNavItems, t]
  );
};
